/**
 * Small pure helpers shared by the three skill-execution components.
 * Kept separate so the components stay focused on layout/interaction.
 */
import type { SkillInvocationStatus } from '../../lib/api-client';

/**
 * Relative time in EITHER direction. Ten callers pass a past timestamp; one —
 * the sandbox lease expiry in the Runtime Logs header — passes a future one,
 * and a past-tense-only formatter rendered it as `-850s ago`: a pod still warm
 * for another 14 minutes read as one that had expired 14 minutes earlier. Past
 * timestamps are unaffected (`now - then` stays positive), so the ten other
 * call sites render exactly as before.
 */
export function relativeTime(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const sec = Math.round((now - then) / 1000);
    const ahead = sec < 0;
    const mag = Math.abs(sec);
    const say = (v: string) => (ahead ? `in ${v}` : `${v} ago`);
    if (mag < 60) return say(`${mag}s`);
    if (mag < 3600) return say(`${Math.round(mag / 60)}m`);
    if (mag < 86400) return say(`${Math.round(mag / 3600)}h`);
    if (mag < 30 * 86400) return say(`${Math.round(mag / 86400)}d`);
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

export function formatDuration(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const sec = Math.floor(ms / 1000);
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

const STATUS_COLORS: Record<SkillInvocationStatus, { fg: string; bg: string; border: string }> = {
  pending: {
    fg: 'var(--accent)',
    bg: 'transparent',
    border: 'var(--accent)',
  },
  completed: {
    fg: 'var(--kind-success, #2da44e)',
    bg: 'transparent',
    border: 'var(--kind-success, #2da44e)',
  },
  failed: {
    fg: 'var(--kind-failure, #cf222e)',
    bg: 'transparent',
    border: 'var(--kind-failure, #cf222e)',
  },
};

export function StatusBadge({ status }: { status: SkillInvocationStatus }) {
  const c = STATUS_COLORS[status];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 7px',
        fontSize: 10,
        fontWeight: 600,
        color: c.fg,
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: 4,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        fontFamily: 'inherit',
      }}
    >
      {status}
    </span>
  );
}

/** Truncate to N chars with an ellipsis if needed. */
export function truncate(s: string | null | undefined, n: number): string {
  if (!s) return '';
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
}
